import React, { useEffect, useRef, useState } from "react";
import "../css/PrivacyPolicy.css";

const SECTIONS = [
  { id: "information-we-collect", title: "Information We Collect" },
  { id: "legal-basis", title: "Legal Basis for Processing" },
  { id: "how-we-use-it", title: "How We Use Information" },
  { id: "sharing", title: "Sharing With Third Parties" },
  { id: "data-storage", title: "Data Storage & Transfers" },
  { id: "your-rights", title: "Your Rights & Choices" },
  { id: "cookies", title: "Cookies" },
  { id: "children", title: "Children's Privacy" },
  { id: "retention", title: "Data Retention" },
  { id: "changes", title: "Changes to This Policy" },
  { id: "contact", title: "Contact & Grievance Officer" },
];

const SCHOOL = {
  name: " Shahara Public School",
  short: "SPS",
  address: "Krishna Nagar Municipality- Ward No. 01, District: Kapilvastu , Lumbini, Nepal",
  phone: "+977-76-520102",
  email: "info@sahara.edu.np",
  grievanceOfficer: "Mrs. Anita Sharma",
  grievanceEmail: "grievance@merashaharpublicschool.edu.in",
  effectiveDate: "1 July 2026",
};

export default function PrivacyPolicy() {
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const [navOpen, setNavOpen] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 },
    );

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) {
        sectionRefs.current[id] = el;
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const handleNavClick = (id) => {
    setNavOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="pp-root">
      {}
      <header className="pp-masthead">
        <div className="pp-masthead-inner">
          <div className="pp-crest" aria-hidden="true">
            <svg viewBox="0 0 64 64" className="pp-crest-svg">
              <path
                d="M32 4 L58 14 V30 C58 46 47 57 32 60 C17 57 6 46 6 30 V14 Z"
                className="pp-crest-shield"
              />
              <text x="32" y="38" textAnchor="middle" className="pp-crest-text">
                {SCHOOL.short}
              </text>
            </svg>
          </div>
          <div className="pp-masthead-copy">
            <p className="pp-eyebrow">{SCHOOL.name}</p>
            <h1 className="pp-title">Privacy Policy</h1>
            <p className="pp-effective">
              Effective from {SCHOOL.effectiveDate}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="pp-nav-toggle"
          onClick={() => setNavOpen((o) => !o)}
          aria-expanded={navOpen}
          aria-controls="pp-toc"
        >
          {navOpen ? "Close contents" : "Contents"}
        </button>
      </header>

      <div className="pp-layout">
        {}
        <nav
          id="pp-toc"
          className={`pp-toc ${navOpen ? "is-open" : ""}`}
          aria-label="Table of contents"
        >
          <p className="pp-toc-label">On this page</p>
          <ol>
            {SECTIONS.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={activeId === s.id ? "is-active" : ""}
                  onClick={() => handleNavClick(s.id)}
                >
                  <span className="pp-toc-index">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.title}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        {}
        <main className="pp-content">
          <p className="pp-intro">
            {SCHOOL.name} ("the School", "we", "us", "our") is committed to
            protecting the privacy of our students, parents, guardians, alumni,
            staff, and website visitors. This policy explains what information
            we collect, why we collect it, how it is used, and the choices you
            have. By using our website or sharing information with the School,
            you agree to the practices described below.
          </p>

          <Section
            id="information-we-collect"
            index={1}
            title="Information We Collect"
          >
            <h3>Information you give us</h3>
            <ul>
              <li>
                Student and parent/guardian name, date of birth, and contact
                details
              </li>
              <li>Admission, academic, attendance, and examination records</li>
              <li>Address, phone number, and email address</li>
              <li>Fee payment and billing details</li>
              <li>
                Health or medical information relevant to a student's wellbeing,
                where voluntarily provided
              </li>
            </ul>
            <h3>Information collected automatically</h3>
            <p>
              When you visit our website, our servers may record standard
              technical details such as your IP address, browser type, the pages
              you view, and the date and time of your visit. This helps us keep
              the site secure and improve how it works.
            </p>
          </Section>

          <Section
            id="legal-basis"
            index={2}
            title="Legal Basis for Processing"
          >
            <p>
              We only process personal information where we have a valid reason
              to do so, such as:
            </p>
            <ul>
              <li>
                Fulfilling our responsibilities as an educational institution
                (admissions, teaching, assessment, and safety)
              </li>
              <li>
                Meeting a legal or regulatory obligation, including those under
                applicable Indian education and data protection law
              </li>
              <li>
                Pursuing a legitimate interest that does not override your
                rights, such as school administration or communication
              </li>
              <li>
                Your consent, for example when you opt in to newsletters or
                photo publication
              </li>
            </ul>
            <p>
              Where processing relies on your consent, you may withdraw it at
              any time; this will not affect any processing already carried out.
            </p>
          </Section>

          <Section id="how-we-use-it" index={3} title="How We Use Information">
            <ul>
              <li>To manage admissions, enrolment, and academic records</li>
              <li>
                To communicate with students, parents, and guardians about
                school activities
              </li>
              <li>
                To ensure student safety, wellbeing, and discipline on campus
              </li>
              <li>To process fee payments and maintain financial records</li>
              <li>
                To comply with reporting obligations to education boards and
                authorities
              </li>
              <li>To improve our website, events, and school programmes</li>
            </ul>
          </Section>

          <Section id="sharing" index={4} title="Sharing With Third Parties">
            <p>We may share limited information with:</p>
            <ul>
              <li>
                Education boards, regulators, and government authorities, as
                required by law
              </li>
              <li>
                Service providers who support school operations, such as
                transport, IT, or payment processors, under confidentiality
                obligations
              </li>
              <li>
                Examination bodies and affiliated institutions for academic
                purposes
              </li>
              <li>
                Law enforcement or courts, where legally required or necessary
                to protect a student's safety
              </li>
            </ul>
            <p>
              We do not sell student or parent information to third parties for
              marketing purposes.
            </p>
          </Section>

          <Section id="data-storage" index={5} title="Data Storage & Transfers">
            <p>
              Records are primarily stored on servers located in India. If any
              information is processed by service providers outside India, we
              take reasonable steps to ensure it receives a comparable level of
              protection.
            </p>
          </Section>

          <Section id="your-rights" index={6} title="Your Rights & Choices">
            <ul>
              <li>
                <strong>Access:</strong> Request a copy of the personal
                information we hold about your child or yourself.
              </li>
              <li>
                <strong>Correction:</strong> Ask us to correct information that
                is inaccurate or outdated.
              </li>
              <li>
                <strong>Restriction:</strong> Ask us to limit certain uses of
                your information, such as marketing communication.
              </li>
              <li>
                <strong>Withdrawal of consent:</strong> Opt out of optional
                communications at any time.
              </li>
              <li>
                <strong>Complaints:</strong> Raise a concern with our Grievance
                Officer, listed below.
              </li>
            </ul>
          </Section>

          <Section id="cookies" index={7} title="Cookies">
            <p>
              Our website may use cookies to remember your preferences and
              understand how visitors use the site. You can disable cookies
              through your browser settings; some site features may not work as
              intended if you do so.
            </p>
          </Section>

          <Section id="children" index={8} title="Children's Privacy">
            <p>
              As a school, we naturally hold information about students who are
              minors. Such information is collected with the consent and
              involvement of a parent or legal guardian and is used solely for
              legitimate educational and administrative purposes. We take extra
              care to protect this information and limit its use accordingly.
            </p>
          </Section>

          <Section id="retention" index={9} title="Data Retention">
            <p>
              We retain student and parent records for as long as necessary to
              fulfil academic, legal, and administrative requirements, including
              periods required after a student leaves the School. Records are
              securely disposed of once they are no longer needed.
            </p>
          </Section>

          <Section id="changes" index={10} title="Changes to This Policy">
            <p>
              We may update this policy from time to time to reflect changes in
              our practices or applicable law. Material changes will be
              communicated through our website or direct notification, and the
              "effective from" date at the top of this page will be updated
              accordingly.
            </p>
          </Section>

          <Section id="contact" index={11} title="Contact & Grievance Officer">
            <p>
              For any questions about this policy or how your information is
              handled, please reach out to us:
            </p>
            <div className="pp-contact-card">
              <p className="pp-contact-name">{SCHOOL.name}</p>
              <p>{SCHOOL.address}</p>
              <p>Phone: {SCHOOL.phone}</p>
              <p>Email: {SCHOOL.email}</p>
              <hr />
              <p className="pp-contact-name">Grievance Officer</p>
              <p>{SCHOOL.grievanceOfficer}</p>
              <p>Email: {SCHOOL.grievanceEmail}</p>
            </div>
          </Section>

          <p className="pp-footer-note">
            This policy is effective as of {SCHOOL.effectiveDate} and applies to{" "}
            {SCHOOL.name} and its official website.
          </p>
        </main>
      </div>
    </div>
  );
}

function Section({ id, index, title, children }) {
  return (
    <section id={id} className="pp-section">
      <div className="pp-section-head">
        <span className="pp-section-index">
          {String(index).padStart(2, "0")}
        </span>
        <h2>{title}</h2>
      </div>
      <div className="pp-section-body">{children}</div>
    </section>
  );
}
